import { useCallback, useEffect, useRef, useState } from 'react'
import { Form } from 'antd'
import { useDispatch } from 'react-redux'
import { setLoading } from 'redux/store/common/slice'

import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { RebateTier } from '../MarketShareRebateCondition/types'
import {
  getRebateTiersRequest,
  getThresholdPeriods,
  updateRebateTiersRequest
} from '../api'
import {
  convertDetailsToForm,
  convertFormToRequestData,
  convertTiersToRequest,
  normalizeRebateTiers
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/utils'
import {
  INITIAL_REBATE_CONDITIONS,
  REBATE_CONDITIONS_TYPE
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/constants'
import { IRebateConditionForm } from 'features/ContractDetails/RebateConditions/RebateConditionsForm/types'
import { TOption } from '../../../../../components/Select/types'

const useRebateConditionsForm = () => {
  const dispatch = useDispatch()
  const contractDetailsContext = useContractDetailsContext()
  const [isChanged, setIsChanged] = useState(false)
  const [tiers, setTiers] = useState<RebateTier[]>([])
  const [thresholdPeriodOptions, setThresholdPeriodOptions] = useState<
    TOption[]
  >([])

  const { details } = contractDetailsContext.state
  const { getContractDetailsAsync, updateContractRebatesAsync } =
    contractDetailsContext.actions

  const [form] = Form.useForm<IRebateConditionForm>()
  const skipTiersFetchRef = useRef(false)

  const type = Form.useWatch('rebate_type', form)
  const applyToHospitals = !!Form.useWatch('apply_to_all_hospitals', form)
  const isAmountProvided = !!Form.useWatch('amount', form)
  const isBaselineSpendProvided = !!Form.useWatch(
    'purchase_volume_threshold',
    form
  )

  const [hasFormChanges, _setHasFormChanges] = useState(false)
  const [hasValidationError, _setHasValidationError] = useState(false)

  const triggerHasFormChanges = useCallback(() => {
    if (!hasFormChanges) {
      _setHasFormChanges(true)
    }
    if (hasValidationError) {
      _setHasValidationError(false)
    }
  }, [hasFormChanges, hasValidationError])

  const saveFormAndUpdateTiers = useCallback(
    async (tiers: RebateTier[]) => {
      skipTiersFetchRef.current = true

      try {
        const values = await form.validateFields()
        const rebateUuid = await updateContractRebatesAsync(
          convertFormToRequestData(values)
        )

        if (!rebateUuid || !details.uuid) return false

        dispatch(setLoading(true))
        await updateRebateTiersRequest(
          details.uuid,
          rebateUuid,
          convertTiersToRequest(tiers, values.threshold_measured_in)
        )

        const [savedDetails, tiersResponse] = await Promise.all([
          getContractDetailsAsync(),
          getRebateTiersRequest(details.uuid, rebateUuid)
        ])
        const savedTiers = normalizeRebateTiers(
          tiersResponse.data,
          savedDetails?.rebate?.threshold_measured_in ??
            values.threshold_measured_in
        )
        setTiers(savedTiers)
        setIsChanged(false)
        _setHasFormChanges(false)

        return true
      } catch (error) {
        if (!(error && typeof error === 'object' && 'errorFields' in error)) {
          console.error(error)
        }
        return false
      } finally {
        skipTiersFetchRef.current = false
        dispatch(setLoading(false))
      }
    },
    [
      details.uuid,
      dispatch,
      form,
      getContractDetailsAsync,
      updateContractRebatesAsync
    ]
  )

  const handleFinishForm = useCallback(
    async (values: IRebateConditionForm) => {
      setIsChanged(false)

      const successCallback = () => {
        _setHasFormChanges(false)
      }

      const requestData = convertFormToRequestData(values)

      await updateContractRebatesAsync(requestData, successCallback)
      // if type = MARKET_SHARE_REBATE and is_tiered is true, update tiers if form was not saved through the tiers popup
      if (values.is_tiered && details.rebate?.uuid) {
        const tiersResponse = await getRebateTiersRequest(
          details.uuid,
          details.rebate?.uuid
        )
        const savedTiers = normalizeRebateTiers(
          tiersResponse.data,
          values.threshold_measured_in
        )
        setTiers(savedTiers)
      }
    },
    [details.rebate?.uuid, details.uuid, updateContractRebatesAsync]
  )

  const onGetThresholdOptions = async (id: string) => {
    const { data } = await getThresholdPeriods(id)
    setThresholdPeriodOptions(
      data.results.map((i) => ({
        value: i.id,
        label: i.value,
        disabled: i.id !== 'quarterly'
      }))
    )
  }

  useEffect(() => {
    if (details.uuid) onGetThresholdOptions(details.uuid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.uuid])

  useEffect(() => {
    if (applyToHospitals) return

    form.setFieldsValue({
      market_share_threshold: null,
      purchase_volume_threshold: null
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyToHospitals])

  useEffect(() => {
    const rebateUuid = details.rebate?.uuid

    if (
      skipTiersFetchRef.current ||
      !details.uuid ||
      !rebateUuid ||
      details.rebate_type !== REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE ||
      !details.rebate?.is_tiered
    ) {
      return
    }

    let cancelled = false

    getRebateTiersRequest(details.uuid, rebateUuid)
      .then(({ data }) => {
        if (cancelled) return
        const nextTiers = normalizeRebateTiers(
          data,
          details.rebate?.threshold_measured_in
        )
        setTiers(nextTiers)
      })
      .catch((error) => console.error(error))

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.uuid, details.rebate?.uuid, details.rebate_type])

  const handleCancelForm = useCallback(() => {
    form.setFieldsValue(convertDetailsToForm(details))

    _setHasFormChanges(false)
  }, [details, form])

  const handleChangeForm = useCallback(() => {
    triggerHasFormChanges()
  }, [triggerHasFormChanges])

  useEffect(() => {
    if (details.rebate_type === type) {
      form.setFieldsValue(convertDetailsToForm(details))

      return
    }

    form.setFieldsValue({ ...INITIAL_REBATE_CONDITIONS[type] })
  }, [details, form, type])

  useEffect(() => {
    form.setFieldsValue(convertDetailsToForm(details))
  }, [details, form])

  return {
    form,
    type,
    isChanged,
    applyToHospitals,
    hasFormChanges,
    hasValidationError,
    handleFinishForm,
    handleCancelForm,
    handleChangeForm,
    thresholdPeriodOptions,
    setIsChanged,
    isAmountProvided,
    isBaselineSpendProvided,
    saveFormAndUpdateTiers,
    tiers
  }
}

export type UseRebateConditionsFormReturnType = ReturnType<
  typeof useRebateConditionsForm
>

export default useRebateConditionsForm
